import React, { useState, useEffect } from 'react';
import './FAQ.css';

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [openQuestions, setOpenQuestions] = useState(new Set());
  const [faqData, setFaqData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFAQData();
  }, []);

  const fetchFAQData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/FAQ/categories');
      if (!response.ok) {
        throw new Error('Failed to fetch FAQ data');
      }
      const data = await response.json();
      setFaqData(data);
      
      // Set the first category as active if available
      if (data.length > 0) {
        setActiveCategory(data[0]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (question) => {
    const newOpenQuestions = new Set(openQuestions);
    if (newOpenQuestions.has(question)) {
      newOpenQuestions.delete(question);
    } else {
      newOpenQuestions.add(question);
    }
    setOpenQuestions(newOpenQuestions);
  };

  const formatAnswer = (answer) => {
    return answer.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < answer.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  if (loading) {
    return (
      <div className="faq-container">
        <div className="faq-header">
          <h1>Frequently Asked Questions</h1>
          <p>Loading...</p>
        </div>
        <div className="loading-spinner">
          <p>Loading FAQ data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="faq-container">
        <div className="faq-header">
          <h1>Frequently Asked Questions</h1>
          <p>Find answers to common questions about PocketPE</p>
        </div>
        <div className="error-message">
          <p>Error loading FAQ data: {error}</p>
          <button onClick={fetchFAQData} className="action-button primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="faq-container">
      <div className="faq-header">
        <h1>Frequently Asked Questions</h1>
        <p>Find answers to common questions about PocketPE</p>
      </div>

      <div className="faq-content">
        <div className="faq-sidebar">
          <h3>Categories</h3>
          <div className="category-list">
            {faqData.map((category) => (
              <button
                key={category.id}
                className={`category-button ${activeCategory?.id === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category)}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        <div className="faq-main">
          {activeCategory ? (
            <>
              <h2>{activeCategory.name}</h2>
              <div className="questions-list">
                {activeCategory.questions.map((item, index) => (
                  <div key={item.id} className="question-item">
                    <button
                      className={`question-button ${openQuestions.has(item.question) ? 'open' : ''}`}
                      onClick={() => toggleQuestion(item.question)}
                    >
                      <span className="question-text">{item.question}</span>
                      <span className="expand-icon">
                        {openQuestions.has(item.question) ? '−' : '+'}
                      </span>
                    </button>
                    {openQuestions.has(item.question) && (
                      <div className="answer-content">
                        {formatAnswer(item.answer)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <h3>No FAQ categories available</h3>
              <p>Please check back later for FAQ content.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FAQ; 